/**
 * OcrolusBankStatementsCard — pick the loan's bank statements and send them
 * into its Ocrolus book (POST /files/bank_statements_to_ocrolus/{dealId}).
 *
 * This used to live only on the Uploaded tab's bulk bar, because the send needs
 * selected document rows and the Ocrolus panel had no table of its own. It has
 * one now: the statement documents are a small, well-defined slice of the loan's
 * files (the Liquidity doc group), so the whole Ocrolus loop — create the book,
 * choose statements, send, read the analysis — happens on one screen. The bulk
 * bar on Uploaded still works; this is an additional entry point, not a move.
 *
 * The backend takes the raw loan-document objects as the body, so rows are
 * passed through untouched.
 */
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SendRounded from "@mui/icons-material/SendRounded";

import Box from "@mui/material/Box";
import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import {
  isOcrolusSupportedDocument,
  OCROLUS_PDF_ONLY_MESSAGE,
} from "../../../../../utils/documents/ocrolusFileValidation";
import colors, { status as statusColors } from "../../../../../theme/colors";
import {
  OCROLUS_COL_DEF,
  OCROLUS_GRID_ROW_H,
  OCROLUS_GRID_SX,
  OcrolusCard,
  OcrolusEmptyState,
  STATE_STYLE,
} from "./ocrolusShared";

// What Ocrolus can actually read. These are `docType` values from the Liquidity
// group in the backend's doc-group registry (see fileDocGroupAndTypesFallback) —
// not a guess: sending a non-statement into a book just produces nothing.
const STATEMENT_DOC_TYPES = [
  "Bank Statement",
  "Brokerage Account Statement",
  "Line of Credit Statement",
  "Other Account Type Statement",
];

const LIQUIDITY_GROUP = "Liquidity";

const DocumentCell = ({ data, onPreview }) => (
  <Flx column g={0.15} sx={{ height: "100%", justifyContent: "center", py: 0.5 }}>
    {/* A displayed filename is always clickable + previewable (CLAUDE.md). */}
    <Txt
      onClick={(e) => {
        e.stopPropagation();
        onPreview(data);
      }}
      sx={{
        fontSize: 13,
        fontWeight: 500,
        color: colors.ink,
        cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "&:hover": { color: colors.action, textDecoration: "underline" },
      }}
    >
      {data?.file_display_name || data?.fileName || "Untitled document"}
    </Txt>
    <Txt sx={{ fontSize: 11, color: colors.slate500 }}>
      {data?.docType || data?.docGroup || "—"}
    </Txt>
  </Flx>
);

const OcrolusBankStatementsCard = ({
  dealId,
  documents,
  state,
  filesSent,
  creating,
  onCreateBook,
  // Doc ids sent from this screen during this session — see `sentDocIds` in
  // LoanOcrolusPanel for why that's the only per-document signal available.
  sentDocIds,
  onSent,
  onGoToUploaded,
}) => {
  const dispatch = useDispatch();
  const { sendBankStatementsToOcrolus } = useLoanFilesHook();
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  // Statements are the default, but a loan occasionally carries one filed under
  // another Liquidity type — this widens the pool rather than hiding it.
  const [allLiquidity, setAllLiquidity] = useState(false);

  const liquidityDocs = useMemo(
    () =>
      (Array.isArray(documents) ? documents : []).filter(
        (d) => d?.docGroup === LIQUIDITY_GROUP,
      ),
    [documents],
  );

  const statementDocs = useMemo(
    () =>
      (Array.isArray(documents) ? documents : []).filter((d) =>
        STATEMENT_DOC_TYPES.includes(d?.docType),
      ),
    [documents],
  );

  const rows = allLiquidity ? liquidityDocs : statementDocs;
  const unsupportedCount = rows.filter(
    (doc) => !isOcrolusSupportedDocument(doc),
  ).length;

  // The widen toggle only earns its place when there is something to widen TO —
  // a Liquidity document filed under a non-statement type. On the common loan
  // (statements and nothing else) it would be a checkbox that changes nothing,
  // so it isn't rendered. It stays visible while checked, or there'd be no way
  // back.
  const showWidenToggle =
    allLiquidity ||
    liquidityDocs.some((d) => !STATEMENT_DOC_TYPES.includes(d?.docType));

  const handlePreview = useCallback(
    (doc) => {
      if (!doc) return;
      dispatch(filePreviewSetData(doc));
    },
    [dispatch],
  );

  const handleSend = useCallback(() => {
    if (!selected.length || !dealId) return;
    setSending(true);
    sendBankStatementsToOcrolus({
      dealId,
      documents: selected,
      onSuccessFn: () => {
        setSending(false);
        onSent?.(selected.map((d) => d?._id).filter(Boolean));
        setSelected([]);
      },
      onFailFn: () => setSending(false),
    });
    // sendBankStatementsToOcrolus is re-created each render (the hook isn't
    // memoized), so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, selected, onSent]);

  const columnDefs = useMemo(
    () => [
      {
        colId: "document",
        headerName: "Document",
        flex: 2,
        minWidth: 240,
        // Checkboxes come from the v33 `rowSelection` object API, which renders
        // its own selection column — mixing in the legacy column-level
        // `checkboxSelection` here would conflict with it.
        valueGetter: (p) => p.data?.file_display_name || "",
        cellRenderer: (p) => (
          <DocumentCell data={p.data} onPreview={handlePreview} />
        ),
      },
      {
        colId: "period",
        headerName: "Period",
        width: 130,
        // `reportEffectiveDate` is the doc's "Valid From" — for a statement
        // that's the statement month. Date-only: formatDate, never new Date().
        valueGetter: (p) =>
          p.data?.reportEffectiveDate
            ? String(p.data.reportEffectiveDate).slice(0, 10)
            : null,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <Txt sx={{ fontSize: 12.5, color: colors.slate600 }}>
              {formatDate(p.value, "—", "MMM yyyy")}
            </Txt>
          </Flx>
        ),
      },
      {
        colId: "uploaded",
        headerName: "Uploaded",
        field: "uploadDate",
        width: 120,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <Txt sx={{ fontSize: 12.5, color: colors.slate500 }}>
              {p.value ? formatDateTime(p.value, "MMM d") : "—"}
            </Txt>
          </Flx>
        ),
      },
      {
        colId: "ocrolus",
        headerName: "Ocrolus",
        width: 120,
        sortable: false,
        // Ocrolus doesn't report which documents ended up in the book, so this
        // can only mark what was sent from this screen in this session. Once the
        // status endpoint returns the per-file list, key it off that instead.
        headerTooltip:
          "Statements sent from this screen. Ocrolus doesn't report per-document membership, so this resets on reload.",
        valueGetter: (p) =>
          !isOcrolusSupportedDocument(p.data)
            ? "unsupported"
            : sentDocIds?.has(p.data?._id)
              ? "sent"
              : "",
        cellRenderer: (p) =>
          p.value === "unsupported" ? (
            <Flx ac sx={{ height: "100%" }}>
              <Tooltip title={OCROLUS_PDF_ONLY_MESSAGE}>
                <Chip
                  size="small"
                  label="PDF required"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    bgcolor: statusColors.warning.bg,
                    color: statusColors.warning.fg,
                  }}
                />
              </Tooltip>
            </Flx>
          ) : p.value === "sent" ? (
            <Flx ac sx={{ height: "100%" }}>
              <Chip
                size="small"
                label="Sent"
                sx={{
                  height: 20,
                  fontSize: 11,
                  bgcolor: statusColors.success.bg,
                  color: statusColors.success.fg,
                }}
              />
            </Flx>
          ) : (
            <Flx ac sx={{ height: "100%" }}>
              <Txt sx={{ fontSize: 12.5, color: colors.slate400 }}>—</Txt>
            </Flx>
          ),
      },
    ],
    [handlePreview, sentDocIds],
  );

  const stateStyle = STATE_STYLE[state] || STATE_STYLE.none;

  return (
    <OcrolusCard
      title="Bank statements"
      endContent={
        <>
          <Txt sx={{ fontSize: 12, color: colors.slate500 }}>
            {selected.length} selected
          </Txt>
          {/* Idempotent server-side, so this stays enabled once a book exists —
              pressing it again returns the same book. */}
          <Button
            variant="outlined"
            size="small"
            startIcon={creating ? null : <AddRounded />}
            onClick={onCreateBook}
            disabled={creating || !dealId}
          >
            {creating ? (
              <CircularProgress size={16} />
            ) : state === "none" ? (
              "Create Ocrolus book"
            ) : (
              "Re-sync book"
            )}
          </Button>
          <Tooltip
            title={
              selected.length
                ? "Send the selected statements to this loan's Ocrolus book"
                : "Select at least one statement"
            }
          >
            {/* span keeps the Tooltip alive while the button is disabled */}
            <span>
              <Button
                size="small"
                startIcon={sending ? null : <SendRounded />}
                onClick={handleSend}
                disabled={sending || !selected.length}
              >
                {sending ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  "Send to Ocrolus"
                )}
              </Button>
            </span>
          </Tooltip>
        </>
      }
      subtitle={
        <Chip
          size="small"
          label={stateStyle.label}
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: stateStyle.bg,
            color: stateStyle.fg,
          }}
        />
      }
    >
      {rows.length === 0 ? (
        <OcrolusEmptyState
          icon={DescriptionOutlined}
          title={
            allLiquidity
              ? "No liquidity documents on this loan"
              : "No bank statements on this loan"
          }
          action={
            onGoToUploaded && (
              <Button variant="text" size="small" onClick={onGoToUploaded}>
                Go to Uploaded
              </Button>
            )
          }
        >
          Upload statements on the Uploaded tab and file them under a Liquidity
          document type — they show up here ready to send.
        </OcrolusEmptyState>
      ) : (
        <AgGridTableWrapper
          compactTable
          noBorder
          height={rows.length > 8 ? 400 : "auto"}
          sx={OCROLUS_GRID_SX}
        >
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            getRowId={(p) => p.data?._id}
            rowSelection={{
              mode: "multiRow",
              isRowSelectable: (node) =>
                isOcrolusSupportedDocument(node.data),
            }}
            onSelectionChanged={(e) => setSelected(e.api.getSelectedRows())}
            rowHeight={OCROLUS_GRID_ROW_H}
            domLayout={rows.length > 8 ? undefined : "autoHeight"}
            defaultColDef={OCROLUS_COL_DEF}
            suppressCellFocus
          />
        </AgGridTableWrapper>
      )}

      <Flx
        ac
        jb
        g={2}
        wrap
        sx={{ px: 2, py: 1, borderTop: `1px solid ${colors.slate200}` }}
      >
        {/* This widens WHICH ROWS ARE LISTED — it is a filter, not a
            select-all. Labelled "Show…" for exactly that reason: as "All
            liquidity documents" it read as an action, and checking it while
            nothing was ticked left Send disabled with no explanation.
            Selecting rows is the grid's own header checkbox. */}
        {showWidenToggle ? (
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small"
                checked={allLiquidity}
                onChange={(e) => setAllLiquidity(e.target.checked)}
                sx={{ py: 0.25 }}
              />
            }
            label={
              <Txt sx={{ fontSize: 12, color: colors.slate600 }}>
                Show all liquidity documents
              </Txt>
            }
          />
        ) : (
          // Keeps the file count on the right when the toggle isn't rendered.
          <Box />
        )}
        <Txt sx={{ fontSize: 12, color: colors.slate500 }}>
          {unsupportedCount
            ? `${unsupportedCount} non-PDF file${unsupportedCount === 1 ? "" : "s"} blocked · `
            : ""}
          {filesSent || 0} file{filesSent === 1 ? "" : "s"} in the book
        </Txt>
      </Flx>
    </OcrolusCard>
  );
};

export default OcrolusBankStatementsCard;
